// SPDX-License-Identifier: AGPL-3.0-or-later

import AppStorage from '@app/features/platform/state/PersistentStorage';
import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import GlassTheme, {GlassTones} from '@app/features/theme/state/GlassTheme';
import {normalizeTheme, type SupportedTheme} from '@app/features/theme/utils/NormalizeTheme';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import type {LinuxAppearanceSnapshot} from '@app/types/electron.d';
import type {ThemeType} from '@fluxer/constants/src/UserConstants';
import {ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

type ExplicitTheme =
	| typeof ThemeTypes.GLASS
	| typeof ThemeTypes.GLASS_LIGHT
	| typeof ThemeTypes.DARK
	| typeof ThemeTypes.LIGHT;

export interface ThemePreferenceSnapshot {
	localTheme: ThemeType;
}

const EXPLICIT_THEMES = new Set<string>([ThemeTypes.GLASS, ThemeTypes.GLASS_LIGHT, ThemeTypes.DARK, ThemeTypes.LIGHT]);
const VALID_THEMES = new Set<string>(Object.values(ThemeTypes));
const STORAGE_KEY = 'theme';

function isExplicitTheme(theme: string | null | undefined): theme is ExplicitTheme {
	return typeof theme === 'string' && EXPLICIT_THEMES.has(theme);
}

function isValidTheme(theme: string | null | undefined): theme is ThemeType {
	return typeof theme === 'string' && VALID_THEMES.has(theme);
}

function loadThemeFromLocalStorage(): ExplicitTheme | null {
	try {
		const stored = AppStorage.getItem(STORAGE_KEY);
		const normalized = isValidTheme(stored) ? normalizeTheme(stored) : null;
		return isExplicitTheme(normalized) ? normalized : null;
	} catch {
		return null;
	}
}

function persistThemeToLocalStorage(theme: string): void {
	try {
		if (isExplicitTheme(theme)) {
			AppStorage.setItem(STORAGE_KEY, theme);
		}
	} catch {}
}

class Theme {
	// KONVO: the theme belongs to the device (2026-10-11), so a phone can follow the system while a
	// laptop stays light. Devices that used to follow the account theme keep it once, on hydrate.
	syncAcrossDevices = false;
	// KONVO: light is the house theme; it matches the landing page and sign-up.
	localTheme: ThemeType = ThemeTypes.LIGHT;
	// The account's theme from before themes were per device; only read when migrating.
	serverTheme: ThemeType = ThemeTypes.LIGHT;
	systemPrefersDark = false;
	private _hydrated = false;
	private _pendingServerTheme: ThemeType | null = null;
	private mediaQuery: MediaQueryList | null = null;
	private localStorageSyncDisposer: (() => void) | null = null;
	private linuxPortalPrefersDark: boolean | null = null;
	private linuxPortalUnsubscribe: (() => void) | null = null;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.initSystemThemeDetection();
		this.initLinuxPortalThemeDetection();
		this.initPersistence();
		// KONVO: Glass "match system" follows the same OS scheme as the rest of the app (incl. Linux).
		reaction(
			() => this.systemPrefersDark,
			(dark) => GlassTheme.setSystemDark(dark),
			{fireImmediately: true},
		);
	}

	get isHydrated(): boolean {
		return this._hydrated;
	}

	get themePreference(): SupportedTheme {
		return normalizeTheme(this.localTheme);
	}

	get effectiveTheme(): ExplicitTheme {
		if (!this._hydrated) {
			const earlyTheme = loadThemeFromLocalStorage();
			if (earlyTheme) {
				return earlyTheme;
			}
		}
		const preference = this.themePreference;
		if (preference === ThemeTypes.SYSTEM) {
			// KONVO: System follows the OS between Paper (Light) and Graphite (Dark). Glass has its own
			// "match system" tone, which flips between its light and dark looks instead.
			return this.systemPrefersDark ? ThemeTypes.DARK : ThemeTypes.LIGHT;
		}
		if (preference === ThemeTypes.GLASS) {
			// KONVO: the glass tone knob (per device) decides between the smoked and the light material.
			const tone = GlassTheme.tone;
			if (tone === GlassTones.LIGHT) return ThemeTypes.GLASS_LIGHT;
			if (tone === GlassTones.SYSTEM && !this.systemPrefersDark) return ThemeTypes.GLASS_LIGHT;
			return ThemeTypes.GLASS;
		}
		return preference;
	}

	setTheme(theme: ThemeType): void {
		if (!isValidTheme(theme)) {
			return;
		}
		this.localTheme = normalizeTheme(theme);
	}

	getPreferenceSnapshot(): ThemePreferenceSnapshot {
		return {localTheme: this.localTheme};
	}

	applyPreferenceSnapshot(snapshot: ThemePreferenceSnapshot): void {
		this.localTheme = isValidTheme(snapshot.localTheme) ? normalizeTheme(snapshot.localTheme) : ThemeTypes.LIGHT;
	}

	updateServerTheme(theme: string | null | undefined): void {
		const normalized = isValidTheme(theme) ? normalizeTheme(theme) : ThemeTypes.LIGHT;
		this.serverTheme = normalized;
		if (!this._hydrated) {
			this._pendingServerTheme = normalized;
		}
	}

	private initSystemThemeDetection(): void {
		if (!window.matchMedia) {
			return;
		}
		this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		this.systemPrefersDark = this.mediaQuery.matches;
		this.mediaQuery.addEventListener('change', this.handleSystemThemeChange);
	}

	private handleSystemThemeChange = (event: MediaQueryListEvent): void => {
		if (this.linuxPortalPrefersDark !== null) {
			return;
		}
		this.systemPrefersDark = event.matches;
	};

	private initLinuxPortalThemeDetection(): void {
		const electronApi = getElectronAPI();
		if (!electronApi || electronApi.platform !== 'linux') {
			return;
		}
		if (!electronApi.getLinuxAppearance || !electronApi.onLinuxAppearanceChanged) {
			return;
		}
		this.linuxPortalUnsubscribe = electronApi.onLinuxAppearanceChanged((snapshot) => {
			this.applyLinuxPortalAppearance(snapshot);
		});
		electronApi
			.getLinuxAppearance()
			.then((snapshot) => {
				this.applyLinuxPortalAppearance(snapshot);
			})
			.catch(() => {});
	}

	private applyLinuxPortalAppearance(snapshot: LinuxAppearanceSnapshot): void {
		runInAction(() => {
			if (snapshot.colorScheme === 'prefer-dark') {
				this.linuxPortalPrefersDark = true;
				this.systemPrefersDark = true;
			} else if (snapshot.colorScheme === 'prefer-light') {
				this.linuxPortalPrefersDark = false;
				this.systemPrefersDark = false;
			} else {
				this.linuxPortalPrefersDark = null;
				if (this.mediaQuery) {
					this.systemPrefersDark = this.mediaQuery.matches;
				}
			}
		});
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'Theme', ['syncAcrossDevices', 'localTheme', 'serverTheme'], {syncAcrossTabs: true});
		runInAction(() => {
			if (this._pendingServerTheme !== null) {
				this.serverTheme = this._pendingServerTheme;
				this._pendingServerTheme = null;
			}
			this.serverTheme = normalizeTheme(this.serverTheme);
			if (this.syncAcrossDevices) {
				// This device followed the account theme until now; keep showing it, from here on its own.
				this.localTheme = this.serverTheme;
				this.syncAcrossDevices = false;
			}
			this.localTheme = normalizeTheme(this.localTheme);
			this._hydrated = true;
		});
		this.localStorageSyncDisposer = reaction(
			() => this.effectiveTheme,
			(theme) => persistThemeToLocalStorage(theme),
			{fireImmediately: true},
		);
	}

	destroy(): void {
		if (this.mediaQuery) {
			this.mediaQuery.removeEventListener('change', this.handleSystemThemeChange);
			this.mediaQuery = null;
		}
		if (this.linuxPortalUnsubscribe) {
			this.linuxPortalUnsubscribe();
			this.linuxPortalUnsubscribe = null;
		}
		if (this.localStorageSyncDisposer) {
			this.localStorageSyncDisposer();
			this.localStorageSyncDisposer = null;
		}
	}
}

export default new Theme();

/**
 * KONVO: pin the light theme on <html> while a screen is mounted (sign-in,
 * sign-up, landing). Returns a cleanup that restores whatever was there.
 */
export function forceLightTheme(): () => void {
	if (typeof document === 'undefined') return () => {};
	const root = document.documentElement;
	const previous = Array.from(root.classList).filter((name) => name.startsWith('theme-'));
	for (const name of previous) root.classList.remove(name);
	root.classList.add('theme-light');
	return () => {
		root.classList.remove('theme-light');
		for (const name of previous) root.classList.add(name);
	};
}
